import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Divider, Tooltip, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Order from '../../models/order';
import Invoice from '../../models/invoice';
import GovernmentInvoice from '../../models/governmentInvoice';

/** Trigger a browser download for a Blob. */
const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const HDDT_LABELS = { draft: 'HDDT nháp', signed: 'HDDT đã ký', cancelled: 'HDDT đã hủy' };

/**
 * Order-page billing actions (FINV-02-S13).
 *
 * The HDDT documents the DELIVERY and belongs to the order — both order types
 * create/sign/download it here. The internal invoice documents the PAYMENT:
 * instant orders get one per order (with the VietQR); package orders are paid
 * through the package invoice, so they only get the classic no-QR image.
 */
const OrderInvoiceActions = ({ order, invoice, onChanged }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [gov, setGov] = useState(null);

  const isInstant = order?.order_type === 'INSTANT_ORDER';
  const alreadySent = order?.status === 'SENT_INVOICE';
  const hddtActionable = order?.status && !['CONFIRMING', 'CANCELLED'].includes(order.status);

  // The order's active HDDT, if any.
  useEffect(() => {
    let active = true;
    if (!order?.id) return undefined;
    (async () => {
      try {
        const found = await new GovernmentInvoice().fetchByOrder(order.id);
        if (active) setGov(found);
      } catch (e) { /* no HDDT yet */ }
    })();
    return () => { active = false; };
  }, [order?.id]);

  // Run an action with a busy guard + toast, then let the parent refresh.
  const act = async (fn, successMessage) => {
    setBusy(true);
    try {
      await fn();
      if (successMessage) toast.success(successMessage);
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.message || 'Thao tác không thành công');
    } finally {
      setBusy(false);
    }
  };

  // --- HDDT: the delivery document (both order types) -----------------------

  const createHddt = () => act(async () => {
    if (!order?.id) throw new Error('Đơn hàng chưa tải xong, thử lại');
    const created = await new GovernmentInvoice().createForOrder(order.id);
    if (!created) throw new Error('Không tạo được hóa đơn điện tử');
    setGov(created);
  }, 'Đã tạo hóa đơn điện tử (nháp)');

  const signHddt = () => act(async () => {
    const signed = await new GovernmentInvoice(gov).sign();
    if (!signed) throw new Error('Không ký được hóa đơn điện tử');
    setGov(signed);
  }, 'Đã ký hóa đơn điện tử');

  const downloadHddt = () => act(async () => {
    const blob = await new GovernmentInvoice(gov).download();
    if (!blob) throw new Error('Không tải được PDF');
    saveBlob(blob, `HDDT-${gov.hddt_sh_don || gov.id}.pdf`);
  });

  // --- internal invoice: the payment document (instant orders only) ---------

  const createInvoice = () => act(async () => {
    if (!order?.id) throw new Error('Đơn hàng chưa tải xong, thử lại');
    const created = await new Invoice().createFromOrder(order.id);
    if (!created) throw new Error('Không tạo được hóa đơn');
  }, 'Đã tạo hóa đơn thanh toán');

  // "Sent" lives on the internal invoice (Matbao has no sent state) and on the order.
  const markAsSent = () => act(async () => {
    if (invoice && invoice.status === 'DRAFT') {
      await new Invoice().updateStatus(invoice.id, 'SENT');
    }
    await new Order({ invoice_url: order.invoice_url || '' }).sendingInvoice(order.id);
  }, 'Đã đánh dấu gửi hóa đơn');

  const downloadInvoiceQr = () => act(async () => {
    const blob = await new Invoice().getPdf(invoice.id);
    if (!blob) throw new Error('Không tạo được hình hóa đơn');
    saveBlob(blob, `${invoice.code}.png`);
  });

  // Package orders are settled by the package — the download must NOT carry a
  // payment QR, so it stays the classic order image.
  const downloadClassicInvoice = () => act(async () => {
    const res = await new Order().getInvoiceById(order.id);
    if (!res?.data) throw new Error('Không tải được hóa đơn');
    saveBlob(new Blob([res.data], { type: 'image/png' }), `${order.name || 'invoice'}.png`);
  });

  if (order?.status === 'CANCELLED') return null;

  const sectionLabel = (text) => (
    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 130 }}>
      {text}
    </Typography>
  );

  return (
    <Box display="flex" flexDirection="column" gap={1.25} width="100%">
      {/* HDDT — the delivery document */}
      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
        {sectionLabel('Hóa đơn điện tử')}
        {gov ? (
          <>
            <Chip
              size="small"
              icon={<ReceiptLongIcon />}
              label={`${HDDT_LABELS[gov.hddt_status] || gov.hddt_status}${gov.hddt_sh_don ? ` · số ${gov.hddt_sh_don}` : ''}`}
              color={gov.hddt_status === 'signed' ? 'success' : 'default'}
            />
            {gov.hddt_status === 'draft' && (
              <Button variant="contained" size="small" disabled={busy} onClick={signHddt}>
                Ký HDDT
              </Button>
            )}
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
              disabled={busy} onClick={downloadHddt}>
              Tải PDF
            </Button>
          </>
        ) : (
          <Tooltip title={hddtActionable ? '' : 'Đơn hàng chưa sẵn sàng để xuất HDDT'}>
            <span>
              <Button variant="contained" size="small" disabled={busy || !hddtActionable}
                onClick={createHddt}>
                Tạo HDDT
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      <Divider flexItem />

      {/* Internal invoice — the payment document */}
      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
        {sectionLabel('Thanh toán')}
        {isInstant ? (
          <>
            {invoice ? (
              <Button variant="contained" size="small" disabled={busy}
                onClick={() => navigate(`/invoices/${invoice.id}`)}>
                Xem hóa đơn {invoice.code}
              </Button>
            ) : (
              <Button variant="contained" size="small" disabled={busy} onClick={createInvoice}>
                Tạo hóa đơn thanh toán
              </Button>
            )}
            {invoice && (
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
                disabled={busy} onClick={downloadInvoiceQr}>
                Tải QR thanh toán
              </Button>
            )}
          </>
        ) : (
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
            disabled={busy} onClick={downloadClassicInvoice}>
            Tải hóa đơn
          </Button>
        )}
        {!alreadySent && (
          <Button variant="outlined" size="small" disabled={busy} onClick={markAsSent}>
            Đánh dấu đã gửi
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default OrderInvoiceActions;
